import {
  Box,
  Button,
  TextField,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import SendIcon from "@mui/icons-material/Send";
import { useAppSelector } from "src/redux/store";
import { useUserType } from "src/hook/useUserType";
import {
  getTargetQueries,
  addTargetQuery,
  updateTargetQueriesByTargetId,
} from "src/utils/api/dashboard";

interface TargetQueryHistoryModalProps {
  targetId: string;
  open: boolean;
  onClose: () => void;
}

export const TargetQueryHistoryModal = ({
  targetId,
  open,
  onClose,
}: TargetQueryHistoryModalProps) => {
  const [query, setQuery] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Get user from Redux store
  const user = useAppSelector((state) => state.root.user.data);
  const userId = user?.userId;
  const { isImplementingOfficer } = useUserType();

  // Fetch target queries
  const {
    data: queriesData,
    isLoading: isLoadingQueries,
    refetch,
  } = useQuery(
    ["targetQueries", targetId],
    async () => {
      try {
        const response = await getTargetQueries(targetId);
        const data = response?.data?.data || [];

        // Convert object with numeric keys to array
        if (data && typeof data === "object" && !Array.isArray(data)) {
          return Object.values(data);
        }

        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error fetching queries:", error);
        return [];
      }
    },
    {
      enabled: open && !!targetId,
      refetchOnMount: true,
      onError: (error: any) => {
        console.error("Error fetching queries:", error);
      },
    }
  );

  // Mutation to add new query
  const { mutate: submitQuery, isLoading: isAddingQuery } = useMutation(
    async () => {
      if (!userId) {
        throw new Error("User ID not found");
      }

      const payload = {
        targetId,
        userId,
        query,
      };

      const response = await addTargetQuery(payload);

      // Only mark prior queries as seen once the Implementing Officer has
      // actually responded with a remark, not merely by opening this modal.
      // A Nodal Officer replying should leave the query PENDING.
      const hasPendingQueries = (queriesData || []).some(
        (item: any) => item.status === "PENDING"
      );
      if (hasPendingQueries && isImplementingOfficer) {
        await updateTargetQueriesByTargetId(targetId, "SEEN");
      }

      return response?.data;
    },
    {
      onSuccess: () => {
        setQuery("");
        setErrorMessage("");
        refetch();
      },
      onError: (error: any) => {
        const errorMsg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to add query";
        setErrorMessage(errorMsg);
        console.error("Error adding query:", error);
      },
    }
  );

  const handleAddQuery = () => {
    if (!query.trim()) {
      setErrorMessage("Query cannot be empty");
      return;
    }
    if (!userId) {
      setErrorMessage("User information not available");
      return;
    }
    submitQuery();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handleAddQuery();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getUserLabelColor = (userType?: string) => {
    const colorMap: { [key: string]: string } = {
      ADMIN: "#d32f2f",
      NODAL_OFFICER: "#f57c00",
      IMPLEMENTING_OFFFICER: "#388e3c",
      LO: "#1976d2",
    };
    return colorMap[userType?.toUpperCase() || ""] || "#757575";
  };

  return (
    <>
      <DialogTitle sx={{ fontWeight: "bold", fontSize: "18px" }}>
        Query History: {/* You can add target name here if needed */}
      </DialogTitle>
      <DialogContent sx={{ minHeight: "500px", maxHeight: "600px" }}>
        {/* Error Alert */}
        {errorMessage && (
          <Alert severity="error" sx={{ marginBottom: "12px" }}>
            {errorMessage}
          </Alert>
        )}

        {/* Queries Display Area */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            marginBottom: "20px",
            maxHeight: "350px",
            overflowY: "auto",
            paddingRight: "8px",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "10px",
            },
          }}
        >
          {isLoadingQueries ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <CircularProgress size={30} />
            </Box>
          ) : queriesData && queriesData.length > 0 ? (
            queriesData.map((item: any) => (
              <Paper
                key={item.id}
                sx={{
                  padding: "16px",
                  backgroundColor: item.name ? "#e8f5e9" : "#f5f5f5",
                  borderLeft: `4px solid ${getUserLabelColor(
                    item.userSubType || item.user?.userType
                  )}`,
                  borderRadius: "4px",
                  marginBottom: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "8px",
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        color: "#333",
                        fontSize: "14px",
                      }}
                    >
                      {item.name || item.user?.name || "Unknown User"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: getUserLabelColor(
                          item.userSubType || item.user?.userType
                        ),
                        fontSize: "11px",
                      }}
                    >
                      {item.userSubType || item.user?.userType || "User"}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: "#999" }}>
                    {formatDate(item.created_at)}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    wordWrap: "break-word",
                    color: "#333",
                    lineHeight: "1.6",
                    fontSize: "14px",
                    marginTop: "8px",
                  }}
                >
                  {item.query}
                </Typography>
              </Paper>
            ))
          ) : (
            <Alert severity="info">No queries found for this target.</Alert>
          )}
        </Box>

        {/* Add New Query Section */}
        <Box sx={{ borderTop: "1px solid #ddd", paddingTop: "16px" }}>
          <Typography
            variant="subtitle2"
            sx={{ marginBottom: "12px", fontWeight: "bold" }}
          >
            Add Further Remarks:
          </Typography>

          <TextField
            fullWidth
            multiline
            placeholder="Type here..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            size="small"
            disabled={isAddingQuery}
            sx={{ marginBottom: "12px" }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "16px" }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        <Button
          onClick={handleAddQuery}
          variant="contained"
          disabled={!query.trim() || isAddingQuery || !userId}
          endIcon={
            isAddingQuery ? <CircularProgress size={20} /> : <SendIcon />
          }
        >
          Submit Remark
        </Button>
      </DialogActions>
    </>
  );
};
